import {isFeatureEnabled} from 'feature-flags';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetUserQuery} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, getUserName, interestSearchParams, queryParams} from '@dms/shared';

import {
  buildArray,
  isNotNilOrEmpty as isNotNilOrEmptyShared,
  Nullish,
  parseDate,
  useQueryState,
} from 'shared/web';

import {getTimeToFirstResponse} from '../utils/getTimeToFirstResponse';
import {useInterestData} from './useInterestData';

export function useInterestParameters(
  interestId: string,
  hasComments: boolean,
  note: string | Nullish
) {
  const [_activeSectionId, setActiveSectionId] = useQueryState(
    queryParams.COMPONENT_SECTIONS_SECTION
  );
  const {interest} = useInterestData(interestId);

  const formatDateTime = useDateTimeFormatter();

  const {timeToFirstResponse, hasEnded} = getTimeToFirstResponse(
    interest,
    interest?.firstResponseAt
  );

  const {data: updatedByUserData} = useGetUserQuery(
    {id: interest?.updatedBy!},
    {skip: isNilOrEmpty(interest?.updatedBy)}
  );

  const onIconClick = () => setActiveSectionId(interestSearchParams.interestDetail.overview);

  return buildArray<Parameter>()
    .when(isNotNilOrEmptyShared(note), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: note,
      onClick: onIconClick,
    })
    .when(hasComments, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: onIconClick,
    })
    .when(isNotNil(interest?.publicId), interest?.publicId)
    .add(
      `${i18n.t('general.labels.created')}: ${
        isNotNilOrEmpty(interest?.createdAt)
          ? formatDateTime('dateTimeShort', parseDate(interest!.createdAt!))
          : EMPTY_PLACEHOLDER
      }`
    )
    .when(
      isNotNilOrEmpty(interest?.updatedAt),
      `${i18n.t('general.labels.changed')}: ${interest?.updatedAt ? formatDateTime('dateTimeShort', parseDate(interest.updatedAt)) : null}`
    )
    .when(
      isNotNilOrEmpty(updatedByUserData) && isNotNilOrEmpty(interest?.updatedBy),
      `${i18n.t('general.labels.changedBy')}: ${getUserName(updatedByUserData)}`
    )
    .when(
      isFeatureEnabled(featureFlags.SALES_INTEREST_LEADQUALITY) &&
        isNotNilOrEmpty(timeToFirstResponse),
      {
        title: timeToFirstResponse,
        icon: 'devices/access_time',
        variant: hasEnded ? 'success' : 'danger',
        tooltip: i18n.t('general.label.timeToFirstResponse'),
      }
    );
}
